import { computed, Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap, shareReplay } from 'rxjs/operators';
import { AuthService as ApiAuthService } from '../../../api/api/auth.service';
import { LoginResponseDto } from '../../../api/model/loginResponseDto';
import { User as ApiUser } from '../../../api/model/user';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { RefreshResponseDto } from '../../../api/model/refreshResponseDto';
import { environment } from '../../../environments/environment';
import { AuthState, LoginCredentials, User, UserRole, UpdateUserRequest } from '../models/user.model';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly apiAuthService = inject(ApiAuthService);
  private readonly AUTH_STORAGE_KEY = 'scalex_auth';
  private refreshTokenRequest$: Observable<RefreshResponseDto> | null = null;

  // Auth state using signals
  private readonly authState = signal<AuthState>(this.loadAuthStateFromStorage());

  // Computed signals for reactive state
  readonly currentUser = computed(() => this.authState().user);
  readonly isAuthenticated = computed(() => this.authState().isAuthenticated);
  readonly userRole = computed(() => this.authState().user?.role ?? null);
  readonly token = computed(() => this.authState().token ?? null);
  readonly refreshToken = computed(() => this.authState().refreshToken ?? null);

  constructor() {
    console.log('[AuthService] Initializing. Current state:', this.authState());
    this.verifyAuth();
  }

  /**
   * Verify authentication state with API
   */
  verifyAuth(): void {
    const state = this.authState();
    if (state.token) {
      this.apiAuthService.authControllerMeV1().subscribe({
        next: (apiUser) => {
          const coreUser = this.mapApiUserToCoreUser(apiUser);
          // Use current token from signal to ensure we use valid tokens if refreshed during request
          this.setAuthenticatedUser(coreUser, this.token()!, this.refreshToken()!);
        },
        error: (err) => {
          console.error('Session verification failed:', err);
          // Only logout if it's a definite authentication error (401 or 403)
          if (err.status === 401 || err.status === 403) {
            this.logout(false);
          } else {
            // For other errors (network, 500), we keep the optimistic state
            // but we might want to show a warning or perform a retry later.
            console.warn('Network or server error during auth verification. Keeping optimistic state.');
          }
        }
      });
    }
  }

  /**
   * Login with email and password
   */
  login(credentials: LoginCredentials): Observable<User> {
    const { email, password } = credentials;

    return this.apiAuthService.authControllerLoginV1({ email, password }).pipe(
      map((response: LoginResponseDto) => {
        const coreUser = this.mapApiUserToCoreUser(response.user);
        this.setAuthenticatedUser(coreUser, response.token, response.refreshToken);
        return coreUser;
      }),
      catchError((error) => {
        console.error('Login failed', error);
        return throwError(() => new Error('Invalid email or password'));
      })
    );
  }

  /**
   * Register new user
   */
  register(userData: { email: string; password: string; firstName: string; lastName: string }): Observable<void> {
    return this.apiAuthService.authControllerRegisterV1(userData).pipe(
      catchError((error) => {
        console.error('Registration failed', error);
        return throwError(() => new Error(error.error?.message || 'Registration failed. Please try again.'));
      })
    );
  }

  /**
   * Request password reset
   */
  forgotPassword(email: string): Observable<void> {
    return this.apiAuthService.authControllerForgotPasswordV1({ email }).pipe(
      catchError((error) => {
        console.error('Forgot password request failed', error);
        return throwError(() => new Error('Failed to send reset email. Please try again.'));
      })
    );
  }

  /**
   * Reset password with token
   */
  resetPassword(hash: string, password: string): Observable<void> {
    return this.apiAuthService.authControllerResetPasswordV1({ hash, password }).pipe(
      catchError((error) => {
        console.error('Password reset failed', error);
        return throwError(() => new Error('Password reset failed. The link may have expired.'));
      })
    );
  }

  /**
   * Update user profile
   */
  updateProfile(data: UpdateUserRequest): Observable<User> {
    return this.apiAuthService.authControllerUpdateV1(data as any).pipe(
      map((apiUser) => {
        const coreUser = this.mapApiUserToCoreUser(apiUser);
        // Update local state with new user data but keep existing token
        const currentState = this.authState();
        if (currentState.token) {
          this.setAuthenticatedUser(coreUser, currentState.token, currentState.refreshToken);
        }
        return coreUser;
      })
    );
  }

  private mapApiUserToCoreUser(apiUser: ApiUser): User {
    console.log('[AuthService] Mapping API User:', apiUser);
    return {
      id: apiUser.id.toString(),
      username: apiUser.username || apiUser.email.split('@')[0],
      email: apiUser.email,
      role: (apiUser.appRole as unknown as UserRole) || UserRole.CLIENT, // Default fallback if role missing
      name:
        apiUser.name ||
        `${apiUser.firstName || ''} ${apiUser.lastName || ''}`.trim() ||
        'Unknown User',
      firstName: apiUser.firstName,
      lastName: apiUser.lastName,
      avatar: apiUser.photo?.path || apiUser.avatar || '8.jpg',
      company: apiUser.company
        ? {
          id: apiUser.company.id.toString(),
          name: apiUser.company.name,
          address: apiUser.company.address ?? undefined,
          website: apiUser.company.website ?? undefined
        }
        : undefined
    };
  }

  /**
   * Set authenticated user
   */
  setAuthenticatedUser(user: User, token: string, refreshToken?: string): void {
    const newState: AuthState = {
      user,
      isAuthenticated: true,
      token,
      refreshToken
    };
    this.authState.set(newState);
    this.saveAuthStateToStorage(newState);

    // Also update UserService signal to reflect current user if needed,
    // although AuthUser is usually separate.
    // this.userService.updateCurrentUser(user);
  }

  /**
   * Logout current user
   */
  logout(navigate: boolean = true): void {
    // Call API logout if token exists
    const token = this.authState().token;
    if (token) {
      this.apiAuthService.authControllerLogoutV1().subscribe({
        error: (err) => console.warn('Logout API failed', err)
      });
    }

    this.authState.set({
      user: null,
      isAuthenticated: false
    });
    this.clearAuthStateFromStorage();

    if (navigate) {
      this.router.navigate(['/login']);
    }
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: UserRole): boolean {
    return this.userRole() === role;
  }

  /**
   * Check if user has any of the provided roles
   */
  hasAnyRole(roles: UserRole[]): boolean {
    const currentRole = this.userRole();
    return currentRole !== null && roles.includes(currentRole);
  }

  /**
   * Check if user can manage users (Super Admin only)
   */
  canManageUsers(): boolean {
    return this.hasRole(UserRole.SUPER_ADMIN);
  }

  /**
   * Check if user can add consultants (Super Admin only)
   */
  canAddConsultants(): boolean {
    return this.hasRole(UserRole.SUPER_ADMIN);
  }

  /**
   * Check if user can add contractors (Super Admin only)
   */
  canAddContractors(): boolean {
    return this.hasRole(UserRole.SUPER_ADMIN);
  }

  /**
   * Check if user can manage projects (Consultant only)
   */
  canManageProjects(): boolean {
    return this.hasRole(UserRole.CONSULTANT);
  }

  /**
   * Check if user can access marketplace (Consultant only)
   */
  canAccessMarketplace(): boolean {
    return this.hasRole(UserRole.CONSULTANT);
  }

  /**
   * Check if user can add clients (Consultant only)
   */
  canAddClients(): boolean {
    return this.hasRole(UserRole.CONSULTANT);
  }

  /**
   * Check if user can access client portal (Client only)
   */
  canAccessClientPortal(): boolean {
    return this.hasRole(UserRole.CLIENT);
  }

  /**
   * Check if user can manage contractor profile (Contractor only)
   */
  canManageContractorProfile(): boolean {
    return this.hasRole(UserRole.CONTRACTOR);
  }

  /**
   * Check if user can view assigned projects (Contractor only)
   */
  canViewAssignedProjects(): boolean {
    return this.hasRole(UserRole.CONTRACTOR);
  }

  /**
   * Refresh the access token
   */
  refreshTokenRequest(): Observable<RefreshResponseDto> {
    if (this.refreshTokenRequest$) {
      return this.refreshTokenRequest$;
    }

    const refreshToken = this.authState().refreshToken;
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    this.refreshTokenRequest$ = this.http.post<RefreshResponseDto>(`${environment.apiUrl}/api/v1/auth/refresh`, {}, {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${refreshToken}`
      })
    }).pipe(
      shareReplay(1),
      tap((response) => {
        if (this.currentUser()) {
          this.setAuthenticatedUser(this.currentUser()!, response.token, response.refreshToken);
        }
        // Clear cached request after success (delayed slightly to handle immediate subsequent calls? No, standard completion is fine)
        // But shareReplay(1) will keep emitting the last value to new subscribers indefinitely if we don't clear it!
        // Actually, we want it to be cleared so NEXT time it fetches fresh.
        // But for concurrent requests occurring NOW, they share.
        // We can use finalize to clear it? No, finalize runs on completion/error.
      }),
      catchError((error) => {
        console.error('Refresh token failed', error);
        this.logout(true);
        this.refreshTokenRequest$ = null;
        return throwError(() => error);
      }),
      finalize(() => {
        // We should clear the observable only after some time or immediately?
        // If we clear immediately, any late subscriber gets null? No.
        // shareReplay keeps the value.
        // A common pattern is:
        this.refreshTokenRequest$ = null;
      })
    );

    return this.refreshTokenRequest$;
  }

  /**
   * Load auth state from localStorage
   */
  private loadAuthStateFromStorage(): AuthState {
    try {
      const stored = localStorage.getItem(this.AUTH_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.token && parsed.user) {
          console.log('[AuthService] Stored auth state found:', parsed);
          return {
            ...parsed,
            isAuthenticated: true
          };
        }
      }
    } catch (error) {
      console.error('Error loading auth state from storage:', error);
    }
    console.warn('[AuthService] No stored auth state found.');
    return { user: null, isAuthenticated: false };
  }

  /**
   * Save auth state to localStorage
   */
  private saveAuthStateToStorage(state: AuthState): void {
    try {
      localStorage.setItem(this.AUTH_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving auth state to storage:', error);
    }
  }

  /**
   * Clear auth state from localStorage
   */
  private clearAuthStateFromStorage(): void {
    try {
      localStorage.removeItem(this.AUTH_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing auth state from storage:', error);
    }
  }
}
